// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { EmptyState } from "../../components/States";
import {
  MessageSquareText, Loader2, RotateCcw, Save, Eye, Search, Mail, Copy, Check, AlertTriangle, Link2, Power,
} from "lucide-react";
import { OwnerLinksAuditCard } from "../../components/admin/OwnerLinksAuditCard";
import { TwilioNumbersDialog } from "../../components/telephony/TwilioNumbersDialog";
import { CallerIdPoolsDialog } from "../../components/telephony/CallerIdPoolsDialog";

const LANG_LABEL = { zh: "中文", en: "English", es: "Español" };
const T = (zh, en, es, lang) => (lang === "zh" ? zh : lang === "es" ? es : en);
const DAYS = [["mon", "周一", "Mon"], ["tue", "周二", "Tue"], ["wed", "周三", "Wed"], ["thu", "周四", "Thu"], ["fri", "周五", "Fri"], ["sat", "周六", "Sat"], ["sun", "周日", "Sun"]];
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1";

/* 通信设置：总开关 → 一级（渠道配置 / 模版配置）→ 二级（短信 / 邮件）→ 渠道配置再分三级（各服务商）。 */
export default function CommsSettings() {
  const { api, lang: ui } = useApp();
  const zh = ui === "zh";
  const [top, setTop] = useState("channels");
  const [channel, setChannel] = useState("sms");
  const [tplChannel, setTplChannel] = useState("sms");
  const [providerId, setProviderId] = useState(null);
  const [ov, setOv] = useState(null);
  const [ovErr, setOvErr] = useState("");

  const loadOverview = useCallback(async () => {
    setOvErr("");
    try {
      const { data } = await api.get("/admin/comms/overview");
      setOv(data);
    } catch (e) {
      setOv(null);
      setOvErr(e?.detailText || e.message || "Failed");
    }
  }, [api]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const providers = ov ? (channel === "sms" ? ov.sms.providers : ov.email.providers) : [];
  useEffect(() => {
    if (!providers.length) return;
    if (!providers.some((p) => p.id === providerId)) setProviderId(providers[0].id);
  }, [providers, providerId]); // eslint-disable-line
  const active = providers.find((p) => p.id === providerId) || null;

  return (
    <div className="space-y-4" data-testid="owner-sms-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquareText className="h-6 w-6 text-indigo-600" />{zh ? "通信设置" : "Communication Settings"}
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">
          {zh ? "自动发送总开关、各渠道服务商密钥与回调、短信与邮件模板。密钥以环境变量兜底，后台填写优先。" : "Master auto-send switch, per-provider credentials and callbacks, SMS and email templates."}
        </p>
      </div>

      {ov && <AutoSendSwitch autoSend={ov.autoSend} api={api} zh={zh} onSaved={loadOverview} />}
      {ov && <ChannelSwitch channels={ov.channels} api={api} zh={zh} canEdit={ov.canEdit} onSaved={loadOverview} />}

      <TabBar testId="comms-top-tabs" value={top} onChange={setTop}
        items={[["channels", zh ? "渠道配置" : "Channels"], ["templates", zh ? "模版配置" : "Templates"]]} />

      {!ov && !ovErr && <div className="py-10 text-center text-slate-400" data-testid="comms-loading"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
      {ovErr && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="comms-error">
          <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0" />
          <p className="text-sm text-amber-800 flex-1 min-w-0">{zh ? `渠道状态加载失败：${ovErr}` : `Failed to load channel status: ${ovErr}`}</p>
          <Button variant="outline" size="sm" onClick={loadOverview} className={FOCUS}>{zh ? "重试" : "Retry"}</Button>
        </div>
      )}

      {ov && top === "channels" && (
        <div className="space-y-4">
          <TabBar testId="comms-channel-tabs" value={channel} onChange={setChannel}
            items={[["sms", zh ? "短信 / 电话" : "SMS / Voice"], ["email", zh ? "邮件" : "Email"]]} />
          <TabBar testId="comms-provider-tabs" value={providerId} onChange={setProviderId} sub
            items={providers.map((p) => [p.id, p.name])} />
          {active && <ProviderPanel provider={active} api={api} zh={zh} canEdit={ov.canEdit} enc={ov.enc} onSaved={loadOverview} />}
          {channel === "email" && ov.email.report && (
            <ReportPanel report={ov.email.report} canEdit={ov.canEdit} api={api} zh={zh} onSaved={loadOverview} />
          )}
        </div>
      )}

      {ov && top === "templates" && (
        <div className="space-y-4">
          <TabBar testId="comms-template-tabs" value={tplChannel} onChange={setTplChannel}
            items={[["sms", zh ? "短信" : "SMS"], ["email", zh ? "邮件" : "Email"]]} />
          {tplChannel === "sms" && <OwnerSmsTemplates api={api} zh={zh} ui={ui} canEdit={ov.canEdit} />}
          {tplChannel === "email" && (
            <Section testId="comms-email-templates" title={zh ? "邮件模板" : "Email templates"} icon={Mail}>
              <EmptyState compact icon={Mail} testId="comms-email-templates-empty"
                title={zh ? "邮件模板尚未接入后台" : "Email templates not wired yet"}
                hint={zh ? "当前邮件正文写在代码里（发票催收、运营周报等）。后续版本会改为可在后台覆盖，届时在此编辑。" : "Email bodies are currently hardcoded. A later release will make them editable here."} />
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- 通用 ---------- */
function TabBar({ items, value, onChange, testId, sub = false }) {
  return (
    <div role="tablist" data-testid={testId} className={`flex flex-wrap gap-1.5 ${sub ? "border-b border-slate-100 pb-2" : ""}`}>
      {items.map(([k, label]) => {
        const on = value === k;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} data-testid={`${testId}-${k}`} data-active={on}
            onClick={() => onChange(k)}
            className={"max-md:min-h-11 max-md:min-w-11 " + (`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 ${FOCUS} ${sub ? "text-xs" : "text-sm"} ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`)}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

const Section = ({ title, icon: Icon, badge, hint, children, testId, bodyClass = "p-4" }) => (
  <section data-testid={testId} className="rounded-lg border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
      <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}{title}
      </h2>
      {badge}
      {hint && <span className="text-xs text-slate-400 ml-auto">{hint}</span>}
    </header>
    <div className={bodyClass}>{children}</div>
  </section>
);

const StatusBadge = ({ ok, zh }) => (
  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
    {ok ? (zh ? "已配置" : "Configured") : (zh ? "未配置" : "Not configured")}
  </span>
);

const RoleBadge = ({ role, zh }) => (
  <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${role === "primary" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>
    {role === "primary" ? (zh ? "主通道" : "Primary") : (zh ? "兜底通道" : "Fallback")}
  </span>
);

const KV = ({ label, value, set, mono = true }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-50 py-2 last:border-0">
    <span className="text-xs text-slate-500 shrink-0">{label}</span>
    <span className={`text-xs text-right break-all ${mono ? "font-mono" : ""} ${set ? "text-slate-800" : "text-slate-400"}`}>{value || (set ? "" : "—")}</span>
  </div>
);

const CopyBtn = ({ text, zh, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error(zh ? "复制失败，请手动选择" : "Copy failed");
    }
  };
  return (
    <button type="button" data-testid={testId} onClick={copy} disabled={!text}
      className={"max-md:min-h-11 max-md:min-w-11 " + (`shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-40 ${FOCUS}`)}>
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}{done ? (zh ? "已复制" : "Copied") : (zh ? "复制" : "Copy")}
    </button>
  );
};

const CallbackList = ({ items, zh, testId }) => (
  <ul className="space-y-2" data-testid={testId}>
    {items.map((c, i) => (
      <li key={c.path} className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
        <span className="text-xs font-medium text-slate-700 shrink-0">{c.label}</span>
        {c.verify && c.verify !== "无" && <span className="rounded bg-white px-1.5 py-0.5 text-[11px] text-slate-500 border border-slate-200">{c.verify}</span>}
        <code className="text-[11px] text-slate-500 break-all flex-1 min-w-[160px]">{c.path}</code>
        <CopyBtn text={c.url} zh={zh} testId={`${testId}-copy-${i}`} />
      </li>
    ))}
  </ul>
);

/* 渠道密钥：DB 覆盖环境变量，加密落库，明文不回显。留空 = 不修改。 */
function SecretRowsEditor({ rows, api, zh, onSaved, testId, readOnly }) {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const pending = Object.entries(draft).filter(([k, v]) => String(v || "").trim() && rows.find((r) => r.key === k)?.editable !== false);
  const save = async () => {
    if (!pending.length) return;
    setBusy(true);
    try {
      await api.put("/admin/comms/secrets", { values: Object.fromEntries(pending) });
      toast.success(zh ? `已保存 ${pending.length} 项，即时生效` : "Saved, effective immediately");
      setDraft({});
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed"));
    }
    setBusy(false);
  };
  const clear = async (key) => {
    setBusy(true);
    try {
      await api.put("/admin/comms/secrets", { clear: [key] });
      toast.success(zh ? "已恢复为环境变量值" : "Restored to env value");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed"));
    }
    setBusy(false);
  };
  return (
    <div data-testid={testId}>
      <div className="space-y-2">
        {rows.map((r) => {
          const ext = r.editable === false; // 由「Twilio 号码配置」等他处页面管理
          return (
            <div key={r.key} className="grid gap-1.5 sm:grid-cols-[150px_1fr_auto] sm:items-center">
              <span className="text-xs text-slate-500 flex items-center gap-1.5 min-w-0">
                <span className="truncate">{r.label}</span>
                <span className={`shrink-0 rounded px-1 text-[10px] ${r.source === "db" ? "bg-indigo-50 text-indigo-700" : ext ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                  {r.source === "db" ? (zh ? "后台" : "db") : ext ? (zh ? "他处管理" : "elsewhere") : "env"}
                </span>
              </span>
              <input
                type={r.secret ? "password" : "text"}
                value={ext ? "" : (draft[r.key] ?? "")}
                onChange={(e) => setDraft((d) => ({ ...d, [r.key]: e.target.value }))}
                placeholder={r.set ? r.value : (zh ? "未设置" : "not set")}
                readOnly={readOnly || ext}
                disabled={ext}
                autoComplete="new-password"
                data-testid={`comms-secret-${r.key}`}
                className={`w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-mono read-only:bg-slate-50 disabled:bg-slate-50 disabled:text-slate-400 ${FOCUS}`}
              />
              <div className="flex items-center gap-1.5">
                {!readOnly && r.source === "db" && (
                  <button type="button" onClick={() => clear(r.key)} disabled={busy} data-testid={`comms-secret-clear-${r.key}`}
                    className={"max-md:min-h-11 max-md:min-w-11 " + (`text-[11px] text-slate-500 underline hover:text-slate-700 disabled:opacity-40 ${FOCUS}`)}>{zh ? "恢复环境变量" : "Restore env"}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {rows.some((r) => r.editable === false) && (
        <p className="mt-2 text-[11px] text-slate-400 leading-relaxed" data-testid={`${testId}-elsewhere-hint`}>
          {zh ? "标记「他处管理」的号码请在「账号权限 → Twilio 号码配置」统一维护，此处仅展示。" : "Items marked “elsewhere” are managed in Users → Twilio numbers."}
        </p>
      )}
      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={save} disabled={busy || !pending.length} data-testid={`${testId}-save`} className={`bg-indigo-600 text-white ${FOCUS}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存密钥" : "Save"}
          </Button>
          <span className="text-xs text-slate-400">{zh ? "留空表示不修改；保存后即时生效，无需重启。" : "Leave blank to keep. Effective immediately."}</span>
        </div>
      )}
    </div>
  );
}

/* ---------- 顶部：自动发送总开关 ---------- */
function AutoSendSwitch({ autoSend, api, zh, onSaved }) {
  const [busy, setBusy] = useState(false);
  const on = autoSend.enabled;
  const toggle = async () => {
    if (!autoSend.canEdit) return;
    const next = !on;
    if (!next && !window.confirm(zh ? "关闭后，由定时任务自动发出的短信与邮件将不再发送（业主进展、周报、催收、分包催办等）。\n数据快照、清理归档、站内提醒照常运行；人工手动发送也不受影响。\n确认关闭？" : "Turning this off stops AUTOMATIC outbound SMS/email (owner digests, weekly reports, collections...).\nSnapshots, cleanup and in-app notices keep running; manual sends are unaffected.\nContinue?")) return;
    setBusy(true);
    try {
      await api.put("/admin/comms/auto-send", { enabled: next });
      toast.success(next ? (zh ? "自动发送已开启" : "Auto-send on") : (zh ? "自动发送已关闭" : "Auto-send off"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed"));
    }
    setBusy(false);
  };
  return (
    <div data-testid="comms-autosend" data-enabled={on ? "1" : "0"}
      className={`rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3 ${on ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50"}`}>
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
        <Power className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800">{zh ? "自动发送总开关" : "Auto-send master switch"}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {autoSend.envDisabled
            ? (zh ? "已被环境变量 DISABLE_SCHEDULER 禁用，页面无法开启" : "Disabled by env var DISABLE_SCHEDULER")
            : autoSend.disabledByChannels
              ? (zh ? "短信与邮件功能总开关已关闭，自动发送随之停用" : "Blocked by the messaging master switch")
              : on
                ? (zh ? "定时任务自动发出的短信 / 邮件正常发送" : "Automatic outbound SMS / email are sent normally")
                : (zh ? "已关闭：定时任务不再自动发出短信 / 邮件（快照、清理、站内提醒照常）" : "Off: automatic outbound SMS / email paused (snapshots & cleanup keep running)")}
        </p>
      </div>
      <button type="button" role="switch" aria-checked={on} disabled={busy || !autoSend.canEdit || autoSend.disabledByChannels} onClick={toggle}
        data-testid="comms-autosend-toggle"
        className={"max-md:min-h-11 max-md:min-w-11 " + (`shrink-0 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 ${FOCUS} ${on ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-600"}`)}>
        <span className={`h-4 w-8 rounded-full relative ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white ${on ? "left-4" : "left-0.5"}`} />
        </span>
        {on ? (zh ? "已开启" : "On") : (zh ? "已关闭" : "Off")}
      </button>
    </div>
  );
}

/* ---------- 顶部：功能总开关（短信 + 邮件，三档：全部开启 / 只关闭外部 / 全部关闭） ---------- */
/**
 * 需求（2026-10-26，老板）：原来只有「开 / 关」两档，公司要求暂停对外群发时只能一刀切关掉，
 * 结果内部通知和工程管理里发给分包商 / 供应商的短信邮件也被掐断（分包签署链接、到货确认等）。
 * 现在改成三档：
 *   - all      → 全部开启：内部、分包商 / 供应商、业主客户都正常发送；
 *   - internal → 只关闭外部：内部通知与分包商 / 供应商照发，业主 / 客户等外部停发（推荐用于「暂停对外群发」）；
 *   - off      → 全部关闭：短信与邮件全部停发，人工点击发送也会被拦，「自动发送」随之失效。
 * 三档用一组 radio 呈现而不是 switch：三态用开关表达会让人分不清「关」是哪一档。
 * testid 契约：容器仍是 comms-channels（data-enabled 保留 0/1 语义，data-mode 给新用例用），
 * 三个按钮为 comms-channels-mode-all / -internal / -off。
 * 说明：旧的 comms-channels-toggle 在本次改造中由三个显式按钮取代 ——
 * 仓库内已确认没有 E2E / 测试引用它（只有本文件自身），故不做别名保留，避免一个元素挂两个 testid 的歧义。
 */
function ChannelSwitch({ channels, api, zh, canEdit, onSaved }) {
  const [busy, setBusy] = useState(false);
  const mode = channels?.mode || (channels?.enabled === false ? "off" : "all");
  const MODES = [
    ["all", zh ? "全部开启" : "All on", zh ? "短信与邮件正常发送：内部、分包商 / 供应商、业主客户都发。" : "Everything sends: internal, subs/suppliers and owners.",
     "border-emerald-300 bg-emerald-50 text-emerald-800"],
    ["internal", zh ? "只关闭外部" : "External off", zh ? "只停业主 / 客户等外部通知；内部同事与分包商 / 供应商照常发送（工程管理不受影响）。" : "Owners/clients paused; internal staff and subs/suppliers keep receiving.",
     "border-amber-300 bg-amber-50 text-amber-800"],
    ["off", zh ? "全部关闭" : "All off", zh ? "短信与邮件全部停发，人工发送也会被拦；自动发送随之失效。" : "All SMS/email blocked, manual sends included; auto-send becomes ineffective.",
     "border-rose-300 bg-rose-50 text-rose-800"],
  ];
  const confirmText = (next) => (next === "off"
    ? (zh ? "关闭后，短信与邮件将全部停止发送 —— 包括人工在系统里点发送的、以及定时任务自动发的。\n「自动发送」总开关也会随之失效。\n确认关闭？" : "Turning this OFF blocks ALL SMS & email, including manual sends.\nThe auto-send switch becomes ineffective.\nContinue?")
    : (zh ? "选择后，发给业主 / 客户等外部联系人的短信与邮件将停发；\n内部同事通知与工程管理里发给分包商 / 供应商的消息仍会正常发送。\n确认切换？" : "Outbound SMS/email to owners/clients will stop.\nInternal notices and engineering messages to subs/suppliers keep sending.\nContinue?"));
  const toggle = async (next) => {
    if (!canEdit || next === mode) return;
    if (next !== "all" && !window.confirm(confirmText(next))) return;
    setBusy(true);
    try {
      await api.put("/admin/comms/channels", { mode: next });
      toast.success({ all: zh ? "已开启全部短信与邮件" : "All SMS & email on",
        internal: zh ? "已切换为「只关闭外部」" : "External messaging off",
        off: zh ? "短信与邮件已全部关闭" : "All messaging off" }[next]);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed"));
    }
    setBusy(false);
  };
  const active = MODES.find(([k]) => k === mode) || MODES[0];
  return (
    <div data-testid="comms-channels" data-enabled={mode !== "off" ? "1" : "0"} data-mode={mode}
      className={`rounded-lg border px-4 py-3 ${mode === "all" ? "border-slate-200 bg-white" : mode === "internal" ? "border-amber-300 bg-amber-50" : "border-rose-300 bg-rose-50"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 gap-0.5 ${mode === "all" ? "bg-emerald-50 text-emerald-700" : mode === "internal" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
          <MessageSquareText className="h-4 w-4" />
          <Mail className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">{zh ? "短信与邮件功能总开关" : "Messaging master switch"}</p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="comms-channels-hint">{active[2]}</p>
        </div>
      </div>
      <div className="mt-3 inline-flex flex-wrap gap-1.5" role="radiogroup" aria-label={zh ? "短信与邮件功能总开关" : "Messaging master switch"}>
        {MODES.map(([k, label, , cls]) => {
          const on = mode === k;
          return (
            <button key={k} type="button" role="radio" aria-checked={on} disabled={busy || !canEdit} onClick={() => toggle(k)}
              data-testid={`comms-channels-mode-${k}`}
              className={"max-md:min-h-11 max-md:min-w-11 " + (`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 ${FOCUS} ${on ? cls + " font-semibold" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`)}>
              <span className={`h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center ${on ? "border-current" : "border-slate-300"}`} aria-hidden="true">
                {on && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
              </span>
              {label}
            </button>
          );
        })}
      </div>
      {mode !== "all" && (
        <p className="mt-2 text-xs text-slate-600" data-testid="comms-channels-scope">
          {mode === "internal"
            ? (zh ? "当前放行：内部员工通知、分包商与供应商（含分包签署链接、变更单、派工提醒）；停发：业主 / 客户等外部联系人。" : "Sending: internal notices, subs/suppliers (signing links, change orders, schedules). Paused: owners/clients.")
            : (zh ? "当前全部停发。若只想暂停对外群发、保留内部与分包/供应商通知，请改选「只关闭外部」。" : "Everything is paused. To keep internal and sub/supplier notices, choose \"External off\".")}
        </p>
      )}
    </div>
  );
}

/* ---------- 服务商面板 ---------- */
function ProviderPanel({ provider, api, zh, canEdit, enc, onSaved }) {
  return (
    <div className="space-y-4">
      <Section testId={`comms-provider-${provider.id}`} title={`${provider.name} ${zh ? "服务商配置" : "provider"}`} icon={Mail}
        badge={<><StatusBadge ok={provider.configured} zh={zh} /><RoleBadge role={provider.role} zh={zh} /></>}
        hint={provider.authMode ? `${zh ? "认证" : "auth"} ${provider.authMode}` : (enc?.configured ? "" : (zh ? "缺少加密密钥" : "no enc key"))}>
        {/* 号码类 key（默认拨出号 / 短信发送号）已由下方「号码清单与 webhook」区块统一维护，
            这里过滤掉它们的只读副本，避免同一个号码在页面上出现两次（一次只读、一次可编辑）。 */}
        <SecretRowsEditor rows={provider.rows.filter((r) => r.editable !== false)} api={api} zh={zh} onSaved={onSaved}
          testId={`comms-secrets-${provider.id}`} readOnly={!canEdit} />
        {provider.id === "resend" && (
          <p className="mt-3 text-xs text-slate-400 leading-relaxed">
            {zh ? "主通道发送失败时会自动改用 SendGrid 兜底，并在发送记录中留痕。" : "If Resend fails, SendGrid is used automatically and the switch is logged."}
          </p>
        )}
        {provider.id === "twilio" && (
          <p className="mt-3 text-xs text-slate-400 leading-relaxed">
            {zh ? "号码清单与 webhook 指向、拨出号码池在下方维护；本区块只管账户密钥。" : "Numbers, webhooks and caller ID pools are managed below; this block covers credentials only."}
          </p>
        )}
        {!canEdit && <p className="mt-3 text-xs text-amber-600">{zh ? "只读：仅管理员 / CEO 可修改" : "Read-only: admin / CEO only"}</p>}
      </Section>

      {/* 号码配置归位：Twilio 的号码清单 / webhook / 拨出号码池原先藏在「账号权限」页的弹窗里，
          现按「通信设置 → 渠道配置 → 短信 / 电话 → Twilio」的层级内嵌到本页（embedded 模式，不带弹窗外壳）。 */}
      {provider.id === "twilio" && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="comms-twilio-numbers">
            <TwilioNumbersDialog lang={zh ? "zh" : "en"} api={api} embedded />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="comms-twilio-pools">
            <CallerIdPoolsDialog lang={zh ? "zh" : "en"} api={api} embedded />
          </div>
        </>
      )}

      {provider.callbacks?.length > 0 && (
        <Section testId={`comms-callbacks-${provider.id}`} title={zh ? "回调地址" : "Callbacks"} icon={Link2}>
          <CallbackList items={provider.callbacks} zh={zh} testId={`comms-callbacks-${provider.id}`} />
        </Section>
      )}
    </div>
  );
}

/* ---------- 邮件：报表收件人与时间 ---------- */
function ReportPanel({ report, canEdit, api, zh, onSaved }) {
  const [recipients, setRecipients] = useState("");
  const [day, setDay] = useState("mon");
  const [hour, setHour] = useState(9);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRecipients(report.recipients || "");
    setDay(report.day || "mon");
    setHour(Number(report.hour ?? 9));
  }, [report]);

  const dirty = recipients !== (report.recipients || "") || day !== (report.day || "mon") || String(hour) !== String(report.hour ?? 9);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/comms/email", { recipients, day, hour: Number(hour) });
      toast.success(zh ? "已保存，报表收件人与时间即时生效" : "Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Failed"));
    }
    setBusy(false);
  };

  return (
    <Section testId="comms-email-report" title={zh ? "报表收件人与时间" : "Report recipients and schedule"} icon={Mail}>
      {!canEdit ? (
        <div className="space-y-2" data-testid="comms-email-report-readonly">
          <KV label={zh ? "收件人" : "Recipients"} value={report.recipients} set={!!report.recipients} />
          <KV label={zh ? "周报时间" : "Schedule"} value={`${report.day} ${report.hour}:00`} set mono={false} />
          <p className="text-xs text-amber-600">{zh ? "只读：仅管理员 / CEO 可修改" : "Read-only: admin / CEO only"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-slate-500">{zh ? "收件人（逗号分隔）" : "Recipients (comma separated)"}</span>
            <input value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="comms-report-recipients"
              placeholder="peter@aligcus.com, alice@aligcus.com"
              className={`mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono ${FOCUS}`} />
          </label>
          <div>
            <span className="text-xs text-slate-500">{zh ? "周报日" : "Day"}</span>
            <div className="mt-1 flex flex-wrap gap-1.5" role="radiogroup" aria-label={zh ? "周报日" : "Day"} data-testid="comms-report-day">
              {DAYS.map(([k, zhLabel, enLabel]) => {
                const on = day === k;
                return (
                  <button key={k} type="button" role="radio" aria-checked={on} data-testid={`comms-report-day-${k}`} data-active={on} onClick={() => setDay(k)}
                    className={"max-md:min-h-11 max-md:min-w-11 " + (`rounded-md border px-2.5 py-1 text-xs ${FOCUS} ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`)}>
                    {zh ? zhLabel : enLabel}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="block max-w-[160px]">
            <span className="text-xs text-slate-500">{zh ? "周报小时（0-23，LA 时区）" : "Hour (0-23, LA)"}</span>
            <input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} data-testid="comms-report-hour"
              className={`mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm ${FOCUS}`} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="comms-report-save" className={`bg-indigo-600 text-white ${FOCUS}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}
            </Button>
            {report.envDefault?.recipients && (
              <button type="button" data-testid="comms-report-use-env" onClick={() => { setRecipients(report.envDefault.recipients); setDay(report.envDefault.day); setHour(Number(report.envDefault.hour)); }}
                className={"max-md:min-h-11 max-md:min-w-11 " + (`text-xs text-slate-500 underline hover:text-slate-700 ${FOCUS}`)}>
                {zh ? "填入环境变量当前值" : "Use env defaults"}
              </button>
            )}
            <span className="text-xs text-slate-400 ml-auto">
              {report.overridden?.reportRecipients || report.overridden?.reportDay || report.overridden?.reportHour
                ? (zh ? "已被后台覆盖" : "overridden")
                : (zh ? "使用环境变量默认" : "using env default")}
            </span>
          </div>
        </div>
      )}
    </Section>
  );
}

/* ---------- 业主短信模板（原样保留行为与 testid） ---------- */
function OwnerSmsTemplates({ api, zh, ui, canEdit }) {
  const [data, setData] = useState(null);
  const [key, setKey] = useState(null);
  const [lang, setLang] = useState("zh");
  const [draft, setDraft] = useState("");
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pq, setPq] = useState("");
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [allProjects, setAllProjects] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/owner-sms/templates"); setData(d); setKey((k) => k || d.items[0]?.key); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const item = useMemo(() => data?.items.find((i) => i.key === key), [data, key]);
  useEffect(() => { if (item) setDraft(item.body[lang] || ""); }, [item, lang]);

  const preview = useCallback(async () => {
    if (!item) return;
    try { const { data: p } = await api.post(`/owner-sms/templates/${item.key}/preview`, { lang, body: draft, projectId: pid || undefined }); setPv(p); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  }, [api, item, lang, draft, pid]);
  useEffect(() => { const t = setTimeout(preview, 300); return () => clearTimeout(t); }, [preview]);

  useEffect(() => {
    if (pq.length < 2) { setProjects([]); return; }
    const run = (list) => setProjects(list.filter((p) => (p.address || "").toLowerCase().includes(pq.toLowerCase()) || (p.clientName || "").toLowerCase().includes(pq.toLowerCase())).slice(0, 8));
    if (allProjects) run(allProjects);
    else api.get("/projects").then(({ data: r }) => { const list = Array.isArray(r) ? r : []; setAllProjects(list); run(list); }).catch(() => {});
  }, [pq, api, allProjects]);

  const save = async (reset = false) => {
    setBusy(true);
    try {
      const { data: r } = await api.put(`/owner-sms/templates/${item.key}`, { lang, body: reset ? "" : draft });
      toast.success(reset ? (zh ? "已恢复默认" : "Reset to default") : (zh ? "已保存" : "Saved"));
      if (reset) setDraft(r.body);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const dirty = item && draft !== (item.body[lang] || "");
  const insertVar = (v) => setDraft((d) => `${d}{${v}}`);

  return (
    <Section testId="comms-sms-templates" title={zh ? "业主短信模板" : "Owner SMS templates"} icon={MessageSquareText}
      hint={data ? (zh ? `共 ${data.items.length} 条` : `${data.items.length} templates`) : ""}>
      {!data && <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
      {data && (
        <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="owner-sms-list">
            {data.items.map((i) => {
              const on = i.key === key;
              return (
                <button key={i.key} type="button" onClick={() => { setKey(i.key); setPv(null); }} data-testid={`owner-sms-item-${i.key}`} data-active={on}
                  className={"max-md:min-h-11 max-md:min-w-11 " + (`w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-0 ${FOCUS} ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`)}>
                  <div className="flex items-center gap-1.5">
                    {on && <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                    <span className={`text-sm ${on ? "font-semibold text-indigo-900" : "font-medium text-slate-800"}`}>{zh ? i.label.zh : i.label.en}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5 pl-0.5">
                    <span className="font-mono">{i.key}</span>
                    {["zh", "en", "es"].map((l) => (
                      <span key={l} className={`px-1 rounded ${i.overridden[l] ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}
                        title={i.overridden[l] ? (zh ? "已自定义" : "customized") : (zh ? "默认" : "default")}>{l.toUpperCase()}</span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          {item && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="owner-sms-editor">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-base font-semibold text-slate-900">{zh ? item.label.zh : item.label.en}</div>
                  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
                    {["zh", "en", "es"].map((l) => (
                      <button key={l} type="button" onClick={() => setLang(l)} data-testid={`owner-sms-lang-${l}`} aria-pressed={lang === l}
                        className={"max-md:min-h-11 max-md:min-w-11 " + (`px-3 py-1.5 ${FOCUS} ${lang === l ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`)}>
                        {LANG_LABEL[l]}{item.overridden[l] && <span className="ml-1 text-[10px] opacity-80">•</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                  {zh ? "占位符：" : "Placeholders:"}
                  {item.vars.map((v) => (
                    <button key={v} type="button" onClick={() => insertVar(v)} data-testid={`owner-sms-var-${v}`}
                      className={"max-md:min-h-11 max-md:min-w-11 " + (`px-1.5 py-0.5 rounded bg-slate-100 font-mono text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 ${FOCUS}`)}>{`{${v}}`}</button>
                  ))}
                </div>
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} readOnly={!canEdit} data-testid="owner-sms-body"
                  className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono leading-relaxed read-only:bg-slate-50 ${FOCUS}`} />
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span data-testid="owner-sms-meta">
                    {item.overridden[lang] ? (zh ? `已自定义 · ${item.updated?.[lang]?.by || ""}` : `Customized · ${item.updated?.[lang]?.by || ""}`) : (zh ? "使用默认文案" : "Default copy")}
                    {!canEdit && <span className="ml-2 text-amber-600">{zh ? "（只读：仅管理员 / CEO 可修改）" : "(read-only)"}</span>}
                  </span>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => save(true)} disabled={busy || !item.overridden[lang]} data-testid="owner-sms-reset" className={FOCUS}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />{zh ? "恢复默认" : "Reset"}
                      </Button>
                      <Button size="sm" onClick={() => save(false)} disabled={busy || !dirty} data-testid="owner-sms-save" className={`bg-indigo-600 text-white ${FOCUS}`}>
                        <Save className="h-3.5 w-3.5 mr-1" />{zh ? "保存" : "Save"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="owner-sms-preview">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                    <Eye className="h-4 w-4 text-indigo-600" />{zh ? "预览" : "Preview"}
                    <span className="text-xs font-normal text-slate-400">{pid ? (zh ? "真实项目变量" : "real project") : (zh ? "示例变量" : "sample values")}</span>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
                    <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder={zh ? "搜索项目地址用真实数据预览…" : "Search a project…"} data-testid="owner-sms-project-q"
                      className={`text-xs rounded-lg border border-slate-200 pl-7 pr-2 py-1.5 w-full ${FOCUS}`} />
                    {projects.length > 0 && pq.length >= 2 && (
                      <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-y-auto">
                        {projects.map((p) => (
                          <button key={p.id} type="button" onClick={() => { setPid(p.id); setPq(""); setProjects([]); }} data-testid={`owner-sms-project-${p.id}`}
                            className={"max-md:min-h-11 max-md:min-w-11 " + (`block w-full text-left px-2.5 py-1.5 text-xs hover:bg-slate-50 ${FOCUS}`)}>
                            {p.address} <span className="text-slate-400">· {LANG_LABEL[p.ownerLang] || "中文"}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {pid && (
                  <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2" data-testid="owner-sms-project-pick">
                    {pv?.project?.address} · {zh ? "业主语言" : "Owner lang"} {LANG_LABEL[pv?.project?.ownerLang] || "—"}
                    <button type="button" onClick={() => setPid("")} className={"max-md:min-h-11 max-md:min-w-11 " + (`text-indigo-600 underline ${FOCUS}`)}>{zh ? "改回示例" : "use sample"}</button>
                  </div>
                )}
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-4 py-3 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap max-w-xl" data-testid="owner-sms-preview-text">
                  {pv?.text || "…"}
                </div>
                {pv && (
                  <div className="text-xs text-slate-500" data-testid="owner-sms-segments">
                    {T(`${pv.chars} 字 · ${pv.encoding} · 约 ${pv.segments} 段`, `${pv.chars} chars · ${pv.encoding} · ~${pv.segments} segment(s)`, `${pv.chars} caracteres · ${pv.encoding} · ~${pv.segments} segmento(s)`, ui)}
                    {pv.segments > 3 && <span className="ml-2 text-amber-600">{zh ? "偏长，建议精简" : "Consider shortening"}</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="mt-4"><OwnerLinksAuditCard api={api} zh={zh} /></div>
    </Section>
  );
}
